/**
 * Route Utilities
 * 
 * Functions for route parsing, path normalization, and state calculation
 */

import { ADMIN_OPS_PREFIX, LEGACY_ROUTE_REDIRECTS, PATH_TO_VIEW, RESET_PASSWORD_PATH, ROUTE_PATHS } from './routes.config'

/**
 * Normalize a URL pathname by trimming slashes and handling edge cases
 * @param {string} pathname - Raw pathname
 * @returns {string} Normalized pathname
 */
export const normalizePathname = (pathname = '') => {
  const rawPath = String(pathname || '').trim()
  if (!rawPath || rawPath === '/') return '/'
  return rawPath.endsWith('/') ? rawPath.slice(0, -1) || '/' : rawPath
}

/**
 * Determine route state for a given pathname
 * Handles legacy redirects, standard routes, auth routes, and admin sections
 * 
 * @param {string} pathname - URL pathname to evaluate
 * @returns {Object} Route state object
 */
export const getRouteState = (pathname = '') => {
  const normalizedPath = normalizePathname(pathname)

  // Check for legacy redirects
  if (LEGACY_ROUTE_REDIRECTS[normalizedPath]) {
    return {
      kind: 'legacy_redirect',
      normalizedPath,
      redirectPath: LEGACY_ROUTE_REDIRECTS[normalizedPath]
    }
  }

  // Check for standard views
  if (PATH_TO_VIEW[normalizedPath]) {
    return {
      kind: 'view',
      normalizedPath,
      viewKey: PATH_TO_VIEW[normalizedPath],
      adminSection: ''
    }
  }

  // Check for password reset
  if (normalizedPath === RESET_PASSWORD_PATH) {
    return {
      kind: 'auth_reset',
      normalizedPath
    }
  }

  // Check for admin section routes
  if (normalizedPath.startsWith(ADMIN_OPS_PREFIX)) {
    const sectionPath = normalizedPath.slice(ADMIN_OPS_PREFIX.length)
    if (!sectionPath || sectionPath.includes('/')) {
      return {
        kind: 'unknown',
        normalizedPath,
        redirectPath: ROUTE_PATHS.home
      }
    }

    return {
      kind: 'view',
      normalizedPath,
      viewKey: 'admin_ops',
      adminSection: sectionPath.toLowerCase()
    }
  }

  // Unknown route - redirect to home
  return {
    kind: 'unknown',
    normalizedPath,
    redirectPath: ROUTE_PATHS.home
  }
}

/**
 * Get route path for a given view
 * @param {string} viewKey - View key (e.g., 'workers', 'admin_ops')
 * @param {string} adminSection - Admin section (only used if viewKey is 'admin_ops')
 * @returns {string} Route path
 */
export const getRouteForView = (viewKey, adminSection = '') => {
  if (viewKey === 'admin_ops' && adminSection) {
    return `${ROUTE_PATHS.admin_ops}/${String(adminSection).trim().toLowerCase()}`
  }

  return ROUTE_PATHS[viewKey] || ROUTE_PATHS.home
}
